import { FC, useEffect, useState } from "react";
import "./ModalAgentsCategoriesLinkFinancials.scss";
import { Button, Card, Col, FormGroup, Row } from "react-bootstrap";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import ModalDefault from "../../../ModalDefault/ModalDefault";
import SkeletonTable from "../../../SkeletonTable/SkeletonTable";
import { customStyles } from "../../../../models/SelectCustomStyles";
import { AgentsCategoryModel } from "../../../../models/Agents/AgentsCategory/AgentsCategoryModel";
import { FinancialAgentCategoryModel } from "../../../../models/Financial/FinancialAgentCategories/FinancialAgentCategoryModel";
import { useFinancials } from "../../../../providers/Financial/Financials/FinancialsProvider";
import { useFinancialAgentCategories } from "../../../../providers/Financial/FinancialAgentCategories/FinancialAgentCategoriesProvider";

interface ModalAgentsCategoriesLinkFinancialsProps {
  show: boolean;
  onClose: () => void;
  category?: AgentsCategoryModel | null;
}

const ModalAgentsCategoriesLinkFinancials: FC<
  ModalAgentsCategoriesLinkFinancialsProps
> = ({ show, onClose, category }) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedFinancials, setSelectedFinancials] = useState<any[]>([]);

  const { setParams: setParamsFinancials, financials } = useFinancials();
  const {
    links,
    isLoadingLinks,
    handleListByAgentCategory,
    handleCreate,
    handleDelete,
  } = useFinancialAgentCategories();

  const linkedFinancialIds = links.map((l) => l.financial_id);

  const financialOptions = financials
    .filter((f) => !f.deleted_at && !linkedFinancialIds.includes(f.id))
    .map((f) => ({ label: f.name ?? "", value: f.id ?? 0 }));

  const reset = () => {
    setSelectedFinancials([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAdd = async () => {
    if (!category?.id || !selectedFinancials.length) {
      toast.warning("Selecione ao menos uma financeira.");
      return;
    }

    setIsSaving(true);
    const results = await Promise.all(
      selectedFinancials.map((f) => handleCreate(f.value, category.id!)),
    );
    setIsSaving(false);

    if (!results.some((ret) => ret)) return;

    toast.success(
      results.length > 1
        ? "Vínculos adicionados com sucesso!"
        : "Vínculo adicionado com sucesso!",
    );
    setSelectedFinancials([]);
    handleListByAgentCategory(category.id);
  };

  const handleRemove = (item: FinancialAgentCategoryModel) => {
    const financialName =
      financials.find((f) => f.id === item.financial_id)?.name ?? "";

    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja remover o vínculo da financeira ${financialName}?</span>`,
      icon: "question",
      confirmButtonText: "Sim, remover!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const ret = await handleDelete(item.id || 0);
        if (!ret) return;
        toast.success("Vínculo removido com sucesso!");
        if (category?.id) handleListByAgentCategory(category.id);
      }
    });
  };

  useEffect(() => {
    if (show && category?.id) {
      reset();
      setParamsFinancials({
        withPaginate: false,
        filter: { only_active: true },
      });
      handleListByAgentCategory(category.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, category?.id]);

  const renderTable = () => {
    if (isLoadingLinks) return <SkeletonTable />;

    return (
      <div className="table-responsive">
        <table className="table table-flush m-0 w-100">
          <thead className="thead-light">
            <tr>
              <th className="text-uppercase opacity-7" scope="col">
                Financeira
              </th>
              <th className="text-uppercase opacity-7 text-center" scope="col">
                Ação
              </th>
            </tr>
          </thead>
          <tbody>
            {links?.length > 0 ? (
              links.map((x: FinancialAgentCategoryModel) => (
                <tr key={x.id} className="text-sm">
                  <td>
                    {financials.find((f) => f.id === x.financial_id)?.name ??
                      `#${x.financial_id}`}
                  </td>
                  <td className="text-center">
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="cursor-pointer"
                      onClick={() => handleRemove(x)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="text-center text-sm">
                  Nenhuma financeira vinculada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <ModalDefault
      title={`Financeiras vinculadas${category?.name ? ` - ${category.name}` : ""}`}
      icon="article"
      show={show}
      onClose={handleClose}
      sizeModal="lg"
      showFooter
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div
        className="ModalAgentsCategoriesLinkFinancials"
        data-testid="ModalAgentsCategoriesLinkFinancials"
      >
        <Row className="align-items-end">
          <Col md={10}>
            <FormGroup className="form-group">
              <label>FINANCEIRA</label>
              <ReactSelect
                isMulti
                isSearchable
                isClearable
                options={financialOptions}
                value={selectedFinancials}
                placeholder="Pesquise por nome ou ID"
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                filterOption={(option, rawInput) => {
                  const input = rawInput.toLowerCase().trim();
                  if (!input) return true;
                  return (
                    option.label.toLowerCase().includes(input) ||
                    String(option.value).includes(input)
                  );
                }}
                onChange={(e) => setSelectedFinancials(e ? [...e] : [])}
              />
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup className="form-group">
              <Button
                variant="primary mb-0 w-100"
                disabled={isSaving}
                onClick={handleAdd}
                title="Adicionar"
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </FormGroup>
          </Col>
        </Row>

        <Card className="mt-2">
          <Card.Header>
            <h5 className="mb-0">Vínculos</h5>
          </Card.Header>
          <Card.Body>{renderTable()}</Card.Body>
        </Card>
      </div>
    </ModalDefault>
  );
};

export default ModalAgentsCategoriesLinkFinancials;
